import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { IconArrowRight } from '@xing-com/icons';
import { BodyCopy } from '@xing-com/typography';

import { SkeletonLoggedOutIndustriesRow } from '../../components/skeleton';
import { useCompaniesIndustries } from '../../hooks/use-companies-industries';
import {
  ROUTES,
  getIndustryRoute,
  getIndustrySearchPageRoute,
} from '../../routes/routes';
import type { SetErrorType } from '../../types/common';
import { formatAmount } from '../../utils/format-amount';
import { getIndustryName } from '../../utils/url';
import * as Styled from './logged-out-landing-page.style';

const LIMIT = 4;

const CompaniesIndustriesModule: React.FC<SetErrorType> = ({ setError }) => {
  const { data, loading, error } = useCompaniesIndustries({
    limit: LIMIT,
  });

  const { isLoggedIn } = useLoginState();
  const intl = useIntl();

  if (error) setError(!!error);
  if (loading) return <SkeletonLoggedOutIndustriesRow />;

  return (
    <Styled.CompaniesRow>
      <Styled.CompaniesTitle size="large">
        <FormattedMessage id={'CP_100_DIRECTORY_TITLE_NEW'} />
      </Styled.CompaniesTitle>
      <Styled.CompaniesContainer>
        {data?.companiesIndustries?.collection?.map((item) => {
          const localizationValue = item?.industry?.localizationValue;
          const id = item?.industry?.id as string;
          const total = item?.total as number;
          const link = isLoggedIn
            ? getIndustrySearchPageRoute({ id })
            : getIndustryRoute({ id, name: getIndustryName({ intl, id }) });
          return (
            <Styled.IndustryLink to={link} key={id}>
              <BodyCopy fontWeight="bold" size="small" noMargin>
                {localizationValue}
              </BodyCopy>
              <BodyCopy size="small" noMargin>
                <FormattedMessage
                  id={'CP_TOTAL_COMPANIES'}
                  values={{ total: formatAmount({ value: total, intl }) }}
                />
              </BodyCopy>
            </Styled.IndustryLink>
          );
        })}
        <Styled.IndustriesShowAllWrapper>
          <Styled.IndustriesButton
            to={ROUTES.industries}
            icon={IconArrowRight}
            iconAfter
            size="small"
          >
            <FormattedMessage id="CP_SEE_ALL_INDUSTRIES" />
          </Styled.IndustriesButton>
        </Styled.IndustriesShowAllWrapper>
      </Styled.CompaniesContainer>
    </Styled.CompaniesRow>
  );
};

export { CompaniesIndustriesModule };
